'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import TeamMemberCard from '@/components/TeamMemberCard'
import LinkWithIcon from '@/components/ui/LinkWithIcon'
import SectionHeader from '@/components/ui/SectionHeader'
import MotionFadeIn from '@/components/ui/MotionFadeIn'
import { CarouselNavButton } from '@/components/ui/CarouselNavButton'
import { getTeamMembers } from '@/utilities/getTeamMembersAction'
import type { Team, TeamOverviewBlock } from '@/payload-types'

interface TeamOverviewClientProps {
  initialMembers: Team[]
  initialPage: number
  hasMore: boolean
  total: number
  header: {
    infoTagText?: string | null
    title: string
    subtitle?: string | null
  }
  ctaLink?: TeamOverviewBlock['ctaLink']
  settings?: {
    anchorId?: string
    backgroundShade?: 'white' | 'neutral-50' | 'neutral-100'
    isDarkMode?: boolean
    padding?: 'sm' | 'md' | 'lg' | 'xl'
    container?: 'container' | 'narrow' | 'full'
  }
}

const DESKTOP_PAGE_SIZE = 3

// Responsive page sizes
const getPageSize = (width: number): number => {
  if (width < 768) return 1 // Mobile
  if (width < 1024) return 2 // Tablet
  return DESKTOP_PAGE_SIZE // Desktop
}

export default function TeamOverviewClient({
  initialMembers,
  initialPage,
  hasMore: initialHasMore,
  header,
  ctaLink,
  settings,
}: TeamOverviewClientProps) {
  const [allLoaded, setAllLoaded] = useState<Team[]>(initialMembers)
  const [currentPage, setCurrentPage] = useState(initialPage)
  const [hasMoreOnServer, setHasMoreOnServer] = useState(initialHasMore)
  const [isLoading, setIsLoading] = useState(false)
  const [pageSize, setPageSize] = useState(DESKTOP_PAGE_SIZE)

  // Detect viewport size and update page size (with debounce)
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null

    const updatePageSize = () => {
      setPageSize(getPageSize(window.innerWidth))
    }

    const debouncedUpdatePageSize = () => {
      if (timeoutId) clearTimeout(timeoutId)
      timeoutId = setTimeout(updatePageSize, 150)
    }

    // Set initial page size immediately after hydration
    updatePageSize()

    window.addEventListener('resize', debouncedUpdatePageSize)
    return () => {
      window.removeEventListener('resize', debouncedUpdatePageSize)
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [])

  // Clamp currentPage when pageSize shrinks the total page count
  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(allLoaded.length / pageSize))
    setCurrentPage((prev) => Math.min(prev, maxPage))
  }, [pageSize, allLoaded.length])

  // Slice visible members from the flat loaded list
  const currentPageMembers = useMemo(() => {
    const start = (currentPage - 1) * pageSize
    return allLoaded.slice(start, start + pageSize)
  }, [allLoaded, currentPage, pageSize])

  const canGoNext = useMemo(() => {
    const nextStart = currentPage * pageSize
    return nextStart < allLoaded.length || hasMoreOnServer
  }, [currentPage, pageSize, allLoaded.length, hasMoreOnServer])

  const goPrev = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1)
    }
  }, [currentPage])

  const goNext = useCallback(async () => {
    const nextStart = currentPage * pageSize

    // If next page already fits in loaded list, just navigate
    if (nextStart < allLoaded.length) {
      setCurrentPage((prev) => prev + 1)
      return
    }

    // Otherwise fetch more (always with DESKTOP_PAGE_SIZE so offsets stay aligned)
    if (!hasMoreOnServer || isLoading) return

    try {
      setIsLoading(true)
      const batchPage = Math.floor(allLoaded.length / DESKTOP_PAGE_SIZE) + 1
      const result = await getTeamMembers(batchPage, DESKTOP_PAGE_SIZE)
      setAllLoaded((prev) => [...prev, ...result.items])
      setHasMoreOnServer(result.hasMore)
      setCurrentPage((prev) => prev + 1)
    } catch (error) {
      console.error('Failed to load more team members:', error)
    } finally {
      setIsLoading(false)
    }
  }, [currentPage, pageSize, allLoaded.length, hasMoreOnServer, isLoading])

  const isDarkMode = settings?.isDarkMode ?? false

  return (
    <div className="relative overflow-hidden">
      <div className="relative z-10">
        {/* Header */}
        <MotionFadeIn initialY={30} duration={0.8}>
          <div className="text-center mb-16">
            <SectionHeader
              align="center"
              title={header.title}
              infoTagText={header.infoTagText || undefined}
              subtitle={header.subtitle || undefined}
            />
          </div>
        </MotionFadeIn>

        {/* Team Members Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto justify-items-center">
          {currentPageMembers.map((member, index) => (
            <TeamMemberCard
              key={`${member.id}-${currentPage}-${index}`}
              teamMember={member}
              index={index}
            />
          ))}
        </div>

        {/* Left/Right Controls */}
        <div className="pointer-events-none select-none">
          <CarouselNavButton
            direction="prev"
            aria-label="Vorherige Team-Seite"
            onClick={goPrev}
            disabled={currentPage === 1}
            className="pointer-events-auto absolute left-4 top-1/2 -translate-y-1/2 z-20"
          />
          <CarouselNavButton
            direction="next"
            aria-label="Nächste Team-Seite"
            onClick={goNext}
            disabled={isLoading || !canGoNext}
            className="pointer-events-auto absolute right-4 top-1/2 -translate-y-1/2 z-20"
          >
            {isLoading ? (
              <div className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            ) : undefined}
          </CarouselNavButton>
        </div>

        {/* Call to Action */}
        {ctaLink && ctaLink.enabled !== false && (
          <MotionFadeIn initialY={30} duration={0.8} delay={0.6}>
            <div className="text-center mt-16">
              <LinkWithIcon
                link={ctaLink}
                variant="link"
                color={isDarkMode ? 'white' : 'brand'}
                size="md"
                className="px-4 py-2"
              />
            </div>
          </MotionFadeIn>
        )}
      </div>
    </div>
  )
}
